import React from "react";
import { Link } from "react-router-dom";
import { colors, fonts } from "../theme";
import { categories } from "../data";
import { Eyebrow, Btn, PhoneLink } from "../ui";
import FaqSection from "../FaqSection";
import Seo from "../../seo/Seo";
import { PAGE_META } from "../../seo/pageMeta";
import { GENERAL_FAQS, EXPORT_FAQS, categoryFaqs } from "../../seo/faqs";
import { webPageSchema, breadcrumbSchema, faqSchema } from "../../seo/schema";

const wrap = { maxWidth: "1180px", margin: "0 auto" };

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "FAQ", path: "/faq" },
];

/**
 * The general buying questions, on their own page.
 *
 * These used to sit on the home page. A dedicated page is better on both
 * counts: the home page gets to stay a conversion page rather than a wall of
 * text, and this page can rank in its own right for the question queries
 * ("how do I choose gsm", "difference between paper and board") that the
 * commercial pages are the wrong shape to win.
 *
 * Product-specific questions deliberately stay on their category and product
 * pages rather than being duplicated here. Two pages answering the same
 * question compete with each other, and the page nearer the product is the one
 * that should win.
 */
export default function Faq() {
  return (
    <main
      className="se-page"
      style={{
        ...wrap,
        padding: "clamp(36px,8vw,60px) var(--sp-gutter) clamp(48px,11vw,84px)",
      }}
    >
      <Seo
        title={PAGE_META.faq.title}
        description={PAGE_META.faq.description}
        path="/faq"
        schema={[
          webPageSchema({
            path: "/faq",
            name: PAGE_META.faq.title,
            description: PAGE_META.faq.description,
            type: "FAQPage",
            breadcrumbId: "https://sigmapapermart.com/faq#breadcrumb",
          }),
          breadcrumbSchema(CRUMBS, "/faq"),
          faqSchema([...GENERAL_FAQS, ...EXPORT_FAQS], "/faq"),
        ]}
      />

      {/* .se-crumbs lets the row wrap below 700px and gives each crumb a 44px
          target. The padding on the link carries that floor above 700px too,
          and the matching negative margin keeps the row's height unchanged. */}
      <nav
        className="se-crumbs"
        aria-label="Breadcrumb"
        style={{ fontSize: "var(--fs-meta)", color: colors.muted2, marginBottom: "18px" }}
      >
        <Link to="/" style={{ display: "inline-block", padding: "10px 4px", margin: "-10px 0", color: colors.brown }}>Home</Link>
        <span style={{ margin: "0 8px" }}>/</span>
        <span aria-current="page">FAQ</span>
      </nav>

      <Eyebrow>Frequently asked questions</Eyebrow>
      <h1
        style={{
          fontFamily: fonts.serif,
          fontSize: "var(--fs-h1)",
          fontWeight: 500,
          margin: "12px 0 12px",
          maxWidth: "22ch",
        }}
      >
        Buying paper &amp; board from Sigma Enterprises
      </h1>
      <p
        style={{
          fontSize: "var(--fs-body)",
          color: colors.muted,
          maxWidth: "62ch",
          margin: "0 0 8px",
        }}
      >
        What printers, converters and traders ask us most often — how we quote,
        what we supply, where we deliver, and how to pick a grade. For questions
        about a specific stock, each category page carries its own.
      </p>

      <FaqSection faqs={GENERAL_FAQS} eyebrow="" title="" style={{ paddingTop: "34px" }} />

      {/* Export gets its own section rather than being mixed into the general
          list. It is a distinct buyer with distinct vocabulary, and a heading
          of its own is what lets the country names sit in an H2 where they
          count, instead of being buried mid-paragraph. */}
      <FaqSection
        faqs={EXPORT_FAQS}
        eyebrow="Export"
        title="Exporting paper and board from India"
        style={{ paddingTop: "56px" }}
      />

      {/* Routes the reader — and a crawler — to the product-specific answers
          rather than repeating them here. */}
      <section style={{ paddingTop: "56px" }}>
        <h2
          style={{
            fontFamily: fonts.serif,
            fontSize: "var(--fs-h3)",
            fontWeight: 500,
            margin: "0 0 8px",
          }}
        >
          Questions about a specific stock
        </h2>
        <p style={{ fontSize: "var(--fs-body)", color: colors.muted, margin: "0 0 20px", maxWidth: "60ch" }}>
          Grade-level questions — gsm for cartons, duplex versus kappa, which
          adhesive face stock to use — are answered on the category pages.
        </p>
        {/* min(280px,100%) stops the track floor from ever exceeding the
            container: a bare 280px minimum stayed 280px inside the 264px
            column a 320px phone leaves, and pushed the document sideways.
            auto-fit rather than auto-fill so a short list leaves no empty
            tracks. */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(280px,100%),1fr))",
            gap: "12px",
          }}
        >
          {categories
            .filter((c) => categoryFaqs(c.slug).length > 0)
            .map((c) => (
              <Link
                key={c.slug}
                to={`/catalogue/${c.slug}`}
                className="se-card"
                style={{
                  cursor: "pointer",
                  border: `1px solid ${colors.border}`,
                  borderRadius: "4px",
                  background: colors.white,
                  padding: "16px 18px",
                }}
              >
                <div style={{ fontSize: "var(--fs-body)", fontWeight: 600 }}>{c.name} →</div>
                <div style={{ fontSize: "var(--fs-meta)", color: colors.muted2, marginTop: "4px" }}>
                  {categoryFaqs(c.slug).length} questions answered
                </div>
              </Link>
            ))}
        </div>
      </section>

      {/* .se-cta trims the 34px/32px padding once the band stacks below 860px;
          .se-cta-band then stretches the button to full width below 560px,
          where a nowrap 180px button sat marooned in a 288px column. */}
      <div
        className="se-cta se-cta-band"
        style={{
          marginTop: "56px",
          padding: "34px 32px",
          border: `1px solid ${colors.border}`,
          borderRadius: "4px",
          background: colors.panel,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "24px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: "var(--fs-body)", color: colors.ink3, maxWidth: "52ch" }}>
          Still not sure which grade you need? WhatsApp us on{" "}
          {/* A bare inline run was a 144x20 target on the one number a phone
              reader is most likely to be reaching for. The negative margin
              cancels the padding so the sentence's line rhythm is unchanged,
              and nowrap stops "+91 99992 42566" splitting across a line
              break — it has two spaces in it, so it could, and half a phone
              number is not a tappable target. */}
          <PhoneLink
            style={{
              color: colors.brown,
              fontFamily: fonts.mono,
              whiteSpace: "nowrap",
              display: "inline-block",
              padding: "12px 4px",
              margin: "-12px -4px",
            }}
          />{" "}
          and describe the job — we will point you to the right stock.
        </div>
        {/* No padding override: Btn's --btn-py/--btn-px already scale with the
            viewport and clear the 44px floor on their own. A fixed 13px/24px
            only pinned the button back to one size. */}
        <Btn variant="dark" to="/contact" style={{ whiteSpace: "nowrap" }}>
          Send an enquiry →
        </Btn>
      </div>
    </main>
  );
}
